<script setup>
import { watch, onBeforeUnmount, computed } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import { TableKitPlus } from 'tiptap-table-plus';
import { Placeholder } from '@tiptap/extensions';
import {
  TextStyle,
  Color,
  FontFamily,
  FontSize,
  LineHeight,
} from '@tiptap/extension-text-style';
import { PaginationPlus, PAGE_SIZES } from 'tiptap-pagination-plus';
import EditorToolbar from './EditorToolbar.vue';
import { VariableNode } from './extensions/VariableNode';
import { PageBreak } from './extensions/PageBreak';
import { Columns, Column } from './extensions/Columns';
import './template-editor.css';

const props = defineProps({
  modelValue: { type: Object, default: null },
  placeholder: { type: String, default: '' },
  readOnly: { type: Boolean, default: false },
  paperSize: { type: String, default: 'letter' },
  orientation: { type: String, default: 'portrait' },
});

const emit = defineEmits(['update:modelValue']);

// Map our paper keys to tiptap-pagination-plus's own PAGE_SIZES constants (px),
// so the editor's visual page sheets use the library's exact dimensions
// (Letter 818x1060, Legal 818x1404, A4 794x1123) and break where the PDF will.
const PAPER_SIZES = {
  letter: PAGE_SIZES.LETTER,
  legal: PAGE_SIZES.LEGAL,
  a4: PAGE_SIZES.A4,
};
const PAGE_MARGIN_PX = 96; // 1in / 72pt — matches the PDF's default @page margin.

const paginationConfig = computed(() => {
  const size = PAPER_SIZES[props.paperSize] || PAPER_SIZES.letter;
  const portrait = props.orientation !== 'landscape';
  return {
    pageWidth: portrait ? size.pageWidth : size.pageHeight,
    pageHeight: portrait ? size.pageHeight : size.pageWidth,
  };
});

const editor = useEditor({
  editable: !props.readOnly,
  content: props.modelValue,
  extensions: [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4] },
      link: { openOnClick: false },
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    LineHeight,
    Highlight.configure({ multicolor: true }),
    Image.configure({ inline: true, allowBase64: true }),
    // Table Plus: enhanced tables that split cleanly across page boundaries (it
    // groups rows into `tableRowGroup` nodes for pagination). Replaces the stock
    // Table/Row/Cell/Header extensions. The wrapper nodes persist in the body JSON
    // but the server renderer treats them transparently (renders the inner rows).
    TableKitPlus,
    Placeholder.configure({ placeholder: () => props.placeholder }),
    VariableNode,
    PageBreak,
    Columns,
    Column,
    // Visual WYSIWYG auto-pagination. Decoration-based (it never mutates the
    // document JSON), so getJSON() stays clean and the server PDF — which does
    // its own pagination — is unaffected. We neutralise its built-in
    // header/footer/page-number chrome; the PDF controls real headers/footers.
    PaginationPlus.configure({
      pageWidth: paginationConfig.value.pageWidth,
      pageHeight: paginationConfig.value.pageHeight,
      marginTop: PAGE_MARGIN_PX,
      marginBottom: PAGE_MARGIN_PX,
      marginLeft: PAGE_MARGIN_PX,
      marginRight: PAGE_MARGIN_PX,
      contentMarginTop: 0,
      contentMarginBottom: 0,
      pageGap: 24,
      pageGapBorderSize: 1,
      pageGapBorderColor: '#e5e7eb',
      // The gap reads as the grey canvas showing between white sheets.
      pageBreakBackground: '#f1f3f5',
      headerLeft: '',
      headerRight: '',
      footerLeft: '',
      footerRight: '',
    }),
  ],
  onUpdate: ({ editor: ed }) => {
    emit('update:modelValue', ed.getJSON());
  },
});

// Reflect external content changes (e.g. loading a saved template) into the editor.
watch(
  () => props.modelValue,
  value => {
    const ed = editor.value;
    if (!ed || !value) return;
    if (JSON.stringify(ed.getJSON()) !== JSON.stringify(value)) {
      ed.commands.setContent(value, { emitUpdate: false });
    }
  }
);

watch(
  () => props.readOnly,
  value => editor.value?.setEditable(!value)
);

// Reflow the visual pages when the paper size / orientation changes.
watch(paginationConfig, cfg => {
  editor.value
    ?.chain()
    .updatePageWidth(cfg.pageWidth)
    .updatePageHeight(cfg.pageHeight)
    .run();
});

onBeforeUnmount(() => editor.value?.destroy());

defineExpose({
  insertVariable: (name, label) => {
    editor.value?.chain().focus().insertVariable({ name, label }).run();
  },
  insertPageBreak: () => {
    editor.value?.chain().focus().insertPageBreak().run();
  },
  insertColumns: (count = 2) => {
    editor.value?.chain().focus().insertColumns(count).run();
  },
  getJSON: () => editor.value?.getJSON(),
});
</script>

<template>
  <div class="flex flex-col h-full template-editor">
    <EditorToolbar v-if="!readOnly && editor" :editor="editor" />
    <div class="overflow-auto flex-1 document-canvas">
      <div class="document-page">
        <EditorContent :editor="editor" />
      </div>
    </div>
  </div>
</template>
